import styled, { css } from 'styled-components';
import { device } from '../breakpoints';
import {
    extraLightCoolGrey,
    fontWeightBold,
    regularBlack,
    regularWhite,
    playGrey15,
    playGrey20,
    cookieYellow2,
    cookieYellow1,
} from '../design-tokens';
import { playFontSize } from '../typography/helpers';
import { Uppercase } from '../typography/PlayUppercase.styles';
import { ButtonSize, ButtonTheme } from './PlayButton';

const backgroundColorLightTheme = 'rgba(255, 255, 255, 0.8)';

const getColors = (color: string, bgColor: string, hoverColor: string, isDisabled: boolean) => css`
    color: ${color};
    background-color: ${bgColor};

    ${!isDisabled &&
    css`
        &:hover {
            background-color: ${hoverColor};
        }
    `}

    &:focus {
        border-color: ${color};
    }

    &:focus:not(:focus-visible) {
        border-color: transparent;
    }
`;

const getTheme = (theme = ButtonTheme.DEFAULT, isDisabled = false) => {
    if (theme === ButtonTheme.LIGHT) {
        return getColors(regularBlack, backgroundColorLightTheme, regularWhite, isDisabled);
    }

    if (theme === ButtonTheme.WHITE) {
        return getColors(regularBlack, regularWhite, extraLightCoolGrey, isDisabled);
    }

    if (theme === ButtonTheme.DARK) {
        return getColors(regularWhite, playGrey15, playGrey20, isDisabled);
    }

    if (theme === ButtonTheme.COOKIE_PRIMARY) {
        return getColors(regularBlack, cookieYellow1, cookieYellow2, isDisabled);
    }

    if (theme === ButtonTheme.COOKIE_SECONDARY) {
        return getColors(regularWhite, 'rgba(254, 248, 197, 0.1)', 'rgba(254, 248, 197, 0.15)', isDisabled);
    }

    return getColors(regularWhite, 'rgba(255, 255, 255, 0.1)', 'rgba(255, 255, 255, 0.15)', isDisabled);
};

const getSize = (size: ButtonSize, rounded = false) => {
    if (rounded) {
        return css`
            border-radius: 50%;
            padding: 10px;
            width: 75px;
            height: 75px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        `;
    }

    switch (size) {
        case ButtonSize.SMALL:
            return css`
                ${playFontSize(1.2)}
                border-radius: 100px;
                padding: 4px 20px;
            `;
        default:
            return css`
                ${playFontSize(1.3)}
                border-radius: 100px;
                padding: 9px 20px;
            `;
    }
};

export type ButtonStylesProps = {
    isDisabled?: boolean;
    size?: ButtonSize;
};

export const ButtonStyles = css`
    ${(props: ButtonStylesProps) => getTheme(ButtonTheme.DEFAULT, props.isDisabled)};
    ${(props: ButtonStylesProps) => getSize(props.size)}
    font-weight: ${fontWeightBold};
    ${Uppercase}
    display: block;
    box-sizing: border-box;
    white-space: nowrap;
    cursor: pointer;
    outline: 0;
    text-decoration: none;
    border: 2px solid transparent;

    &:focus,
    &:hover,
    &:active {
        text-decoration: none;
        outline: 0;
    }
`;

type ButtonLinkProps = {
    theme: ButtonTheme;
    isDisabled: boolean;
    size: ButtonSize;
    isCentered: boolean;
    rounded?: boolean;
};

export const ButtonLink = styled.a<ButtonLinkProps>`
    ${ButtonStyles}
    ${(props: ButtonLinkProps) => getTheme(props.theme, props.isDisabled)}
    ${(props: ButtonLinkProps) => getSize(props.size, props.rounded)}
    
    text-align: center;

    ${(props: ButtonLinkProps) =>
        props.isDisabled &&
        css`
            opacity: 0.4;
        `}
    ${(props: ButtonLinkProps) =>
        props.isCentered &&
        css`
            @media ${device.GteSmall} {
                display: inline-block;
            }
        `}
`;

type StyledAnchorProps = {
    theme?: ButtonTheme;
    isDisabled?: boolean;
    size?: ButtonSize;
    isCentered?: boolean;
    rounded?: boolean;
};

export const StyledAnchor = styled.a<StyledAnchorProps>`
    ${ButtonStyles}
    ${(props: StyledAnchorProps) => getTheme(props.theme, props.isDisabled)}
    ${(props: StyledAnchorProps) => getSize(props.size, props.rounded)}

    text-align: center;

    ${(props: StyledAnchorProps) => props.isDisabled && 'opacity: 0.4;'}
    ${(props: StyledAnchorProps) =>
        props.isCentered &&
        css`
            @media ${device.GteSmall} {
                display: inline-block;
            }
        `}
`;

type StyledButtonProps = {
    theme?: ButtonTheme;
    isDisabled?: boolean;
    size?: ButtonSize;
    isCentered?: boolean;
    rounded?: boolean;
};

export const StyledButton = styled.button<StyledButtonProps>`
    ${ButtonStyles}
    ${(props: StyledButtonProps) => getTheme(props.theme, props.isDisabled)}
    ${(props: StyledButtonProps) => getSize(props.size, props.rounded)}
    text-align: center;

    ${(props: StyledButtonProps) => props.isDisabled && 'opacity: 0.4;'}
    ${(props: StyledButtonProps) =>
        props.isCentered &&
        css`
            @media ${device.GteSmall} {
                display: inline-block;
            }
        `}
`;

// Centers Button
export const ButtonContainer = styled.div`
    display: flex;
    @media ${device.GteSmall} {
        justify-content: center;
    }

    button {
        flex-grow: 1;
        @media ${device.GteSmall} {
            flex-grow: 0;
        }
    }
`;

export const ButtonLabel = styled.span`
    vertical-align: middle;
    line-height: 1.4;
`;
